import { useEffect, useState } from "react";
import { getPodColor } from "./podColors";
import { formatTime, localDateKey, getEventKind, shortDay } from "./dateUtils";
import { makeNamer, Dialog, PlusIcon, BackIcon, PenIcon, TrashIcon } from "./Pods";

// Contacts tab (view === "contacts"), split out of Calendar.js and redesigned 2026-10-10. Display only:
// the contacts subscription and createContact / updateContact / deleteContact / inviteMember stay in
// Calendar.js. Contacts are private to their owner.

function SearchIcon() {
  return <svg className="pg-icon" viewBox="0 0 24 24" aria-hidden="true"><circle cx="11" cy="11" r="6.5" /><path d="M20 20l-4-4" /></svg>;
}

function ContactForm({ initial = {}, submitLabel, onSubmit, onCancel, onDelete }) {
  const [firstName, setFirstName] = useState(initial.firstName || "");
  const [lastName, setLastName] = useState(initial.lastName || "");
  const [email, setEmail] = useState(initial.email || "");
  const submit = async (e) => {
    e.preventDefault();
    if (!firstName.trim() || !email.trim()) return;
    await onSubmit(firstName.trim(), lastName.trim(), email.trim());
  };
  return (
    <form className="pg-form" onSubmit={submit}>
      <div className="pg-form__fields">
        <label>First name<input className="form-input" value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder="Jane" autoFocus /></label>
        <label>Last name<input className="form-input" value={lastName} onChange={(e) => setLastName(e.target.value)} placeholder="Smith" /></label>
        <label className="pg-form__full">Email<input className="form-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="email@example.com" /></label>
      </div>
      <div className="pg-form__actions">
        {onDelete && <button type="button" className="btn btn--ghost btn--sm pg-danger" onClick={onDelete}><TrashIcon />Delete contact</button>}
        <span className="pg-form__end">
          {onCancel && <button type="button" className="btn btn--secondary btn--sm" onClick={onCancel}>Cancel</button>}
          <button type="submit" className="btn btn--primary btn--sm" disabled={!firstName.trim() || !email.trim()}>{submitLabel}</button>
        </span>
      </div>
    </form>
  );
}

export default function Contacts({
  contacts, pods, invitations, currentUserEmail, isPodOwner,
  allEventsForDisplay, isEventVisibleToUser,
  onCreateContact, onUpdateContact, onDeleteContact, onInviteMember, onOpenEvent,
}) {
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false); // phones: the contact page replaces the list
  const [editing, setEditing] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [podMenu, setPodMenu] = useState(false);
  const { initialsOf } = makeNamer(contacts, currentUserEmail);

  // "Add to a pod" menu: close on outside click or Esc
  useEffect(() => {
    if (!podMenu) return;
    const onDown = (e) => { if (!e.target.closest?.(".pg-menuwrap")) setPodMenu(false); };
    const onKey = (e) => { if (e.key === "Escape") setPodMenu(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDown); document.removeEventListener("keydown", onKey); };
  }, [podMenu]);

  const label = (c) => (c.name && c.name.trim()) || c.email;
  const sorted = [...contacts].sort((a, b) => label(a).localeCompare(label(b), undefined, { sensitivity: "base" }));
  const q = search.trim().toLowerCase();
  const shown = q ? sorted.filter((c) => label(c).toLowerCase().includes(q) || (c.email || "").toLowerCase().includes(q)) : sorted;
  const current = sorted.find((c) => c.id === selectedId) || sorted[0] || null;
  const podsWith = (email) => Object.values(pods).filter((p) => (p.members || []).includes(email));
  const open = (id) => { setSelectedId(id); setDetailOpen(true); setEditing(false); setPodMenu(false); document.querySelector(".app-main")?.scrollTo(0, 0); };

  const todayKey = localDateKey(new Date());

  const renderDetail = (c) => {
    const shared = podsWith(c.email);
    // Pods you own that they're not in (and not already invited to)
    const invitable = Object.values(pods).filter((p) => isPodOwner(p) && !(p.members || []).includes(c.email) &&
      !invitations.some((inv) => inv.podId === p.id && inv.inviteeEmail === c.email && inv.status === "pending"));
    const plans = allEventsForDisplay
      .filter((ev) => isEventVisibleToUser(ev) && getEventKind(ev) === "event" && (ev.endDate || ev.startDate) >= todayKey)
      .filter((ev) => (ev.sharedPods || []).length && ((ev.memberEmails || []).includes(c.email) || ev.ownerEmail === c.email))
      .sort((a, b) => (a.startDate + (a.startTime || "")).localeCompare(b.startDate + (b.startTime || "")))
      .slice(0, 4);
    return (
      <section className="card pg-detail" aria-label={label(c)}>
        <button type="button" className="pg-back" onClick={() => setDetailOpen(false)}><BackIcon />Contacts</button>
        <div className="pg-dhead pg-dhead--contact">
          <span className="pg-face pg-face--xl">{initialsOf(c.email)}</span>
          <div className="pg-dhead__text">
            <h2 className="pg-title">{label(c)}</h2>
            {label(c) !== c.email && <div className="pg-sub">{c.email}</div>}
          </div>
        </div>
        <div className="pg-actions">
          {invitable.length > 0 && (
            <span className="pg-menuwrap">
              <button type="button" className="btn btn--secondary pg-btn" aria-expanded={podMenu} onClick={() => setPodMenu(!podMenu)}><PlusIcon />Add to a pod</button>
              {podMenu && (
                <span className="pg-menu" role="menu">
                  {invitable.map((p) => {
                    const col = getPodColor(p.id);
                    return (
                      <button key={p.id} type="button" role="menuitem" onClick={async () => { setPodMenu(false); await onInviteMember(p.id, c.email); }}>
                        <i style={{ background: col.border }} />{p.name}
                      </button>
                    );
                  })}
                </span>
              )}
            </span>
          )}
          {!editing && <button type="button" className="btn btn--ghost pg-btn" onClick={() => setEditing(true)}><PenIcon />Edit</button>}
        </div>

        {editing && (
          <ContactForm
            key={c.id}
            initial={{ firstName: c.firstName || c.name?.split(" ")[0] || "", lastName: c.lastName || c.name?.split(" ").slice(1).join(" ") || "", email: c.email }}
            submitLabel="Save"
            onSubmit={async (f, l, e) => { await onUpdateContact(c.id, f, l, e); setEditing(false); }}
            onCancel={() => setEditing(false)}
            onDelete={async () => { await onDeleteContact(c.id); setSelectedId(null); setEditing(false); setDetailOpen(false); }}
          />
        )}

        <div className="pg-sec">
          <div className="dash-eyebrow">Pods you share</div>
          {shared.length === 0 ? <p className="dash-muted">None yet.</p> : (
            <div className="pg-chips">
              {shared.map((p) => {
                const col = getPodColor(p.id);
                return <span key={p.id} className="pg-podchip" style={{ background: col.bg, color: col.text }}>{p.name}</span>;
              })}
            </div>
          )}
        </div>
        <div className="pg-sec">
          <div className="dash-eyebrow">Plans together</div>
          {plans.length === 0 ? <p className="dash-muted">Nothing coming up.</p> : plans.map((ev) => (
            <button key={ev.id} type="button" className="pg-plan" onClick={(e) => onOpenEvent(ev, e.currentTarget)}>
              <span className="pg-date"><small>{ev.startDate === todayKey ? "Today" : shortDay(ev.startDate)}</small><b>{Number(ev.startDate.slice(8))}</b></span>
              <span className="pg-plan__text">{ev.title}<small>{ev.startTime ? formatTime(ev.startTime) : "All day"}{ev.location ? ` · ${ev.location}` : ""}</small></span>
            </button>
          ))}
        </div>
      </section>
    );
  };

  let letter = "";
  return (
    <div className="pg">
      <div className="pg-head">
        <h1 className="section-title pg-h1">Contacts{contacts.length > 0 && <span className="pg-count">{contacts.length}</span>}</h1>
        <div className="pg-head__actions">
          <label className="pg-search"><SearchIcon /><input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name or email" aria-label="Search contacts" /></label>
          <button type="button" className="btn btn--primary pg-btn" onClick={() => setShowAdd(true)}><PlusIcon />Add contact</button>
        </div>
      </div>

      <div className={`pg-two${detailOpen ? " pg-two--open" : ""}`}>
        <section className="card pg-list pg-list--scroll" aria-label="Contacts">
          {contacts.length === 0 && <p className="dash-muted pg-pad">No contacts yet. Add the people you make plans with.</p>}
          {q && shown.length === 0 && <p className="dash-muted pg-pad">No contacts match “{search.trim()}”.</p>}
          {shown.map((c) => {
            const L = label(c)[0].toUpperCase();
            const head = L !== letter ? (letter = L) : null;
            const sharedPods = podsWith(c.email);
            return (
              <div key={c.id} className="pg-group">
                {head && <div className="pg-letter">{head}</div>}
                <button type="button" className="pg-row" aria-current={current?.id === c.id} onClick={() => open(c.id)}>
                  <span className="pg-face">{initialsOf(c.email)}</span>
                  <span className="pg-row__text">
                    <b>{label(c)}</b>
                    <small>
                      {sharedPods.length ? sharedPods.map((p) => <i key={p.id} className="pg-dot" style={{ background: getPodColor(p.id).border }} />) : null}
                      {sharedPods.length ? sharedPods.map((p) => p.name).join(", ") : c.name && c.name.trim() ? c.email : ""}
                    </small>
                  </span>
                </button>
              </div>
            );
          })}
        </section>

        {current ? renderDetail(current) : (
          <section className="card pg-detail pg-detail--empty"><p className="dash-muted">Pick a contact to see the pods and plans you share.</p></section>
        )}
      </div>

      {showAdd && (
        <Dialog title="Add contact" onClose={() => setShowAdd(false)}>
          <ContactForm submitLabel="Add contact"
            onSubmit={async (f, l, e) => { await onCreateContact(f, l, e); setShowAdd(false); }} />
        </Dialog>
      )}
    </div>
  );
}
